/**
 * Phase 31 — what happens when the list is weak, in one sentence with two
 * inputs — "each check shrinks the next drops by [X] %, never under [N]
 * SMS" — and the ladder down it means for THIS container's default amount.
 * The floor is a tenth of the base when left empty, never under one SMS.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import type { DropGenTextMix } from '@trafficpulse/shared';

import { HintDot, NumberField } from '@/components/ui';

import type { DraftScaling } from '../../../../drop-generator.types';
import { ProvenTag } from '../../../shared/texts/ProvenTag';
import { INPUT_CLASS } from '../../general/fields/field-chrome';

const LADDER_STEPS = 4;

/** The engine's own floor: the set one, else a tenth of the base, never under 1. */
export const floorOf = (base: number, minAmount: number | null) => minAmount ?? Math.max(1, Math.round(base * 0.1));

/** 800 → 600 → 450 → 338 → 253 → 80 (floor): the first steps down, floored. */
export function shrinkLadder(base: number, shrinkPct: number, minAmount: number | null): string[] {
  const floor = floorOf(base, minAmount);
  const out: string[] = [base.toLocaleString('en-US')];
  for (let step = 1; step <= LADDER_STEPS; step += 1) {
    // The engine's own formula: base × (1 − pct)^steps, rounded once, never under the floor.
    const next = Math.round(base * Math.pow(1 - shrinkPct / 100, step));
    if (next <= floor) {
      out.push(`${floor.toLocaleString('en-US')} (floor)`);
      return out;
    }
    out.push(next.toLocaleString('en-US'));
  }
  return [...out, '…'];
}

export const ShrinkAction: FC<{
  scaling: DraftScaling;
  defaultAmount: number;
  onPatch: (patch: Partial<DraftScaling>) => void;
  /** Phase 37 — the texts shrunk drops send (mirror). */
  proven?: DropGenTextMix;
}> = ({ scaling, defaultAmount, onPatch, proven }) => (
  <div className="flex flex-col gap-1.5 text-xs text-muted-foreground">
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1.5">
      the next drops shrink by
      <NumberField
        value={scaling.shrinkPct}
        onChange={(v) => onPatch({ shrinkPct: Math.min(90, Math.max(1, v)) })}
        min={1}
        max={90}
        className={`${INPUT_CLASS} h-7 w-16 text-xs`}
        aria-label="Shrink per weak check, percent"
      />
      % per check with new results · never under
      <input
        type="number"
        min={1}
        max={1000000}
        value={scaling.minAmount ?? ''}
        placeholder={`${floorOf(defaultAmount, null).toLocaleString('en-US')} (a tenth)`}
        onChange={(e) => onPatch({ minAmount: e.target.value === '' ? null : Number(e.target.value) })}
        className={`${INPUT_CLASS} h-7 w-28 text-xs ${scaling.minAmount === null ? 'text-muted-foreground' : ''}`}
        aria-label="Floor for shrunk drops"
      />
      SMS
      <HintDot label="One step down per check; a weak check wins over a performing one on the same numbers, a terrible one over both. A step down undoes a step up first (the steps are one count). Empty floor = a tenth of the base amount, never under one SMS; ten steps at most. Only unsent drafts shrink — sent ones never change. A new day starts at step 0." />
      <ProvenTag mix={proven} what="The shrunk drops" />
    </div>
    <p className="font-mono text-[11px] tabular-nums text-muted-foreground/80">
      {defaultAmount.toLocaleString('en-US')} per drop → {shrinkLadder(defaultAmount, scaling.shrinkPct, scaling.minAmount).slice(1).join(' → ')}
    </p>
  </div>
);
